import { DefaultGameState, Notebook } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/** CSS affecting the {@link NotebookComponent} */
const style: string = css`
    @keyframes hover {
        0% { transform: scale(1.02); }
        50% { transform: scale(1.07); }
        100% { transform: scale(1.02); }
    }

    .notebook-btn {
        position: fixed;
        z-index: 1;
        width: 130px;
        height: 100px;
        margin-top: 1.5%;
        margin-left: 1.5%;
        background-color: rgb(15, 122, 237);
        box-shadow: -4px 0 0 0 white,
            4px 0 0 0 white,
            0 -4px 0 0 white,
            0 4px 0 0 white;
        transition: background-color 0.3s ease-in-out;
    }

    .notebook-btn:hover {
        background-color: black;
        animation: hover 0.5s ease-in-out 2 forwards;
    }
       
    .notebook-btn img {
        width: 100%;
    }

    .notebook {
        background-image: url("/assets/img/rooms/notebook2.png");
        background-color: rgb(15, 122, 237);
        background-repeat: no-repeat;
        background-position: center;
        background-size:90% 90%;
        width: 40%;
        height: 50%;
        box-shadow: -10px 0 0 0 white,
            10px 0 0 0 white,
            0 -10px 0 0 white,
            0 10px 0 0 white;
    }

    .close-btn {
        width: 50px;
        height: 50px;
        background-color: rgb(166, 38, 38);
        box-shadow: -6px 0 0 0 white,
            6px 0 0 0 white,
            0 -6px 0 0 white,
            0 6px 0 0 white;
        font-family: "pixel";
        font-size: 50px;
        margin-left: auto;
        z-index: 1;
        transition: background-color 0.3s ease-in-out;

    }

    .close-btn:hover {
        background-color: rgb(98, 18, 18);
        animation: hover 0.5s ease-in-out 2 forwards;

    }

    header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    textarea {
        font-family: "pixel";
        font-size: 100%;
        background-color: transparent;
        background-repeat: no-repeat;
        background-size: cover;
        color: rgb(79, 64, 57);
        border: none;
        resize: none;
    }

    .page-1 {
        position: absolute; 
        top: 12.5%; 
        left: 11%;
        width: 35%; 
        height: 75%;
    }

    .page-2 {
        position: absolute; 
        top: 12.5%; 
        right: 10%;
        width: 35%; 
        height: 75%;
    }


    @media (max-width: 2500px) {
        .notebook {
            width: 60%;  
            height: 50%; 
        }
    }
`;

/**
 * Represents the notebook web component
 */
export class NotebookComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /**
     * Constructor of the web component
     */
    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });

        await this.render();
    }

    /**
     * Render the notebook component
     */
    private async render(): Promise<void> {
        const state: DefaultGameState = (await this._gameRouteService.getGameState()) as DefaultGameState;
        if (!this.shadowRoot || state.roomAlias === "startup") {
            return;
        }

        const notebook: HTMLDialogElement = await this.renderNotebook() as HTMLDialogElement;
        const elements: HTMLElement[] = htmlArray`
            <style>
                ${style}
            </style>
            ${this.renderNotebookBtn(notebook)}
            ${notebook}
        `;
        this.shadowRoot.append(...elements);
    }

    /**
     * Renders the notebook button
     *
     * @param notebook - The dialog representing the notebook
     * @returns - The button that triggers the notebook dialog
     */
    private renderNotebookBtn(notebook: HTMLDialogElement): HTMLElement {
        const element: HTMLElement = html`
            <button class="notebook-btn">
                <img src="/assets/img/rooms/notebook.png">
            </button>
        `;

        element.addEventListener("click", () => notebook.showModal());

        return element;
    }

    /**
     * Renders the notebook dialog with text ares for notes
     * Sets up close button, limits text input and renders the saved content
     *
     * @returns - The notebook  dialog element
     */
    private async renderNotebook(): Promise<HTMLElement> {
        const element: HTMLDialogElement = this.createNotebookDialog();
        this.setupCloseBtn(element);
        this.limitText(element);
        await this.renderNotebookContent(element);

        return element;
    }

    /**
     * Creates notebook dialog with text areas and close button
     *
     * @returns - The created dialog element
     */
    private createNotebookDialog(): HTMLDialogElement {
        return html`
        <dialog class="notebook">
            <header>
                <button class="close-btn">X</button>
            </header>
            <section class="content">
                <textarea class="page-1" placeholder="This is your notebook..."></textarea>
                <textarea class="page-2" placeholder="Write any notes..."></textarea>
            </section>
        </dialog>
    ` as HTMLDialogElement;
    }

    /**
     * Sets up the close button for the dialog
     * Closes and saves the content when closed.
     *
     * @param element - The element to set up the close button for
     */
    private setupCloseBtn(element: HTMLDialogElement): void {
        const closeBtn: HTMLButtonElement = element.querySelector(".close-btn") as HTMLButtonElement;
        closeBtn.addEventListener("click", async () => {
            element.close();
            await this.updateNotebookContent(element);
        });
    }

    /**
     * Limits the text input to prevent oveflow
     *
     * @param element - The notebook dialog
     */
    private limitText(element: HTMLDialogElement): void {
        element.querySelectorAll("textarea").forEach(textarea => {
            textarea.addEventListener("input", () => {
                while (textarea.scrollHeight > textarea.clientHeight) {
                    textarea.value = textarea.value.slice(0, -1);
                }
            });
        });
    }

    /**
     * Renders the previously saved content
     * Retrieves data from backendm
     *
     * @param element - The notebook dialog
     */
    private async renderNotebookContent(element: HTMLDialogElement): Promise<void> {
        const pageOne: HTMLTextAreaElement | null = element.querySelector(".page-1");
        const pageTwo: HTMLTextAreaElement | null = element.querySelector(".page-2");

        if (!pageOne || !pageTwo) return;

        const notebookContent: Notebook = await this._gameRouteService.getNotebookContent();

        pageOne.value = notebookContent.pageOne ?? "";
        pageTwo.value = notebookContent.pageTwo ?? "";
    }

    /**
     * Updates the current notebook content
     * Sends the updated content to the backend
     *
     * @param element - The dialog element
     */
    private async updateNotebookContent(element: HTMLDialogElement): Promise<void> {
        const pageOneValue: string = (element.querySelector(".page-1") as HTMLTextAreaElement).value;
        const pageTwoValue: string = (element.querySelector(".page-2") as HTMLTextAreaElement).value;

        const updatedContent: Notebook = {
            pageOne: pageOneValue,
            pageTwo: pageTwoValue,
        };

        await this._gameRouteService.updateNotebookContent(updatedContent);
    }
}
